import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const ThisWeekUpdatesSection = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Elfsightスクリプトの読み込み
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      // クリーンアップ
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <section className="py-32 bg-gradient-to-b from-white via-gray-50 to-white" id="this-week">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-6 py-2 rounded-full text-sm font-bold mb-6">
            <i className="ri-flashlight-line"></i>
            LIVE UPDATES
          </div>
          <h2 className="text-6xl md:text-7xl font-black text-gray-900 mb-6 leading-tight">
            {i18n.language === 'ja' ? '今週の動き' : 'This Week\'s Updates'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {i18n.language === 'ja' 
              ? 'リアルタイムで更新される、吉野の最新情報をチェック' 
              : 'Check the latest updates from Yoshino, updated in real-time'}
          </p>
        </motion.div>

        {/* Updates Stack */}
        <div className="space-y-12">
          {/* Google Calendar - Full Width */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative group"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 via-red-500/20 to-orange-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-3xl transition-all duration-500">
              <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-2xl">
                      <i className="ri-calendar-event-line text-4xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-black text-white mb-1">
                        {i18n.language === 'ja' ? 'イベント' : 'Events'}
                      </h3>
                      <p className="text-white/90 text-lg font-medium">
                        {i18n.language === 'ja' ? '次回の予定をチェック' : 'Check Upcoming Schedule'}
                      </p>
                    </div>
                  </div>
                  <a
                    href="/consultation"
                    className="hidden md:flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-full font-bold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {i18n.language === 'ja' ? '参加する' : 'Join Events'}
                    <i className="ri-arrow-right-line text-xl"></i>
                  </a>
                </div>
              </div>
              <div className="p-8">
                <div className="elfsight-app-b58a027f-a655-45ed-989c-4df5fce22ee0" data-elfsight-app-lazy></div>
              </div>
              <button
                onClick={() => {
                  const element = document.getElementById('join');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="md:hidden flex items-center justify-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 font-bold hover:from-orange-700 hover:to-red-700 transition-all duration-300 cursor-pointer w-full"
              >
                {i18n.language === 'ja' ? 'イベントに参加する' : 'Join Events'}
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
          </motion.div>

          {/* note Feed & Instagram Feed - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* note Feed */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-green-500/20 via-teal-500/20 to-green-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-3xl transition-all duration-500 h-full flex flex-col">
                <div className="bg-gradient-to-r from-green-600 via-teal-600 to-green-600 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl">
                        <i className="ri-article-line text-3xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-black text-white mb-0.5">note</h3>
                        <p className="text-white/90 text-sm font-medium">
                          {i18n.language === 'ja' ? '深掘り記事で本質を知る' : 'In-depth Articles'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6 flex-1">
                  <div className="elfsight-app-e1bf271b-ea68-434a-9262-7678988be48c" data-elfsight-app-lazy></div>
                </div>
                <a 
                  href="https://note.com/yoshinovillage/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 font-bold hover:from-green-700 hover:to-teal-700 transition-all duration-300 cursor-pointer"
                >
                  {i18n.language === 'ja' ? 'もっと見る' : 'View More'}
                  <i className="ri-arrow-right-up-line text-xl"></i>
                </a>
              </div>
            </motion.div>

            {/* Instagram Feed */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-3xl transition-all duration-500 h-full flex flex-col">
                <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl">
                        <i className="ri-instagram-line text-3xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-black text-white mb-0.5">Instagram</h3>
                        <p className="text-white/90 text-sm font-medium">
                          {i18n.language === 'ja' ? '日々の活動をリアルタイムで' : 'Daily Activities in Real-time'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6 flex-1">
                  <div className="elfsight-app-5f6f9443-2df6-4438-8cf5-ed3d7b5fd99e" data-elfsight-app-lazy></div>
                </div>
                <a 
                  href="https://www.instagram.com/yoshino_village_project/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 font-bold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 cursor-pointer"
                >
                  {i18n.language === 'ja' ? 'もっと見る' : 'View More'}
                  <i className="ri-arrow-right-up-line text-xl"></i>
                </a>
              </div>
            </motion.div>
          </div>

          {/* YouTube Feed - Full Width */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative group"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-3xl transition-all duration-500">
              <div className="bg-gradient-to-r from-red-600 via-orange-600 to-red-600 p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-2xl">
                      <i className="ri-youtube-line text-4xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-black text-white mb-1">
                        Yoshino Now Voice
                      </h3>
                      <p className="text-white/90 text-lg font-medium">
                        {i18n.language === 'ja' ? '吉野の今を動画で' : 'Yoshino Now on Video'}
                      </p>
                    </div>
                  </div>
                  <a
                    href="https://www.youtube.com/@YoshinoNowVoice"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:flex items-center gap-2 bg-white text-red-600 px-6 py-3 rounded-full font-bold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {i18n.language === 'ja' ? 'もっと見る' : 'View More'}
                    <i className="ri-arrow-right-up-line text-xl"></i>
                  </a>
                </div>
              </div>
              <div className="p-8">
                <div className="elfsight-app-b0f7eb2e-fe7c-4660-8146-d39e4af3cbd6" data-elfsight-app-lazy></div>
              </div>
              <a
                href="https://www.youtube.com/@YoshinoNowVoice"
                target="_blank"
                rel="noopener noreferrer"
                className="md:hidden flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-orange-600 text-white py-4 font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 cursor-pointer w-full"
              >
                {i18n.language === 'ja' ? 'もっと見る' : 'View More'}
                <i className="ri-arrow-right-up-line text-xl"></i>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ThisWeekUpdatesSection;
